import { X_CACHEDQUERY_ID } from '../../../constants';
import { requestDatasetAction } from '../../../api/requests/dataset/requestDatasetAction';
import {
  DatasetControlAggregateActionApiRequestData,
  Headers,
} from '../../../api/types/types';
import { apiRequest } from '../../../api/utils/apiRequest';
import { detailedApiMethodFetcher } from '../../../api/utils/fetchers/detailedApiMethodFetcher';
import {
  AggregateControlAction,
  DatasetControl,
  DetailedApiMethod,
  SimpleApiMethod,
} from '../../../types/types';
import { ResponseActionHandler } from '../../../types/context';
import { resolveTemplate } from '../../../utils/json/resolveTemplate';
import useApiErrorHandling from '../../useApiErrorHandling';
import { executeWithForceRetry } from './executeWithForceRetry';

export const getAggregateApiCallback = (
  control: DatasetControl,
  token: string | null,
  requestData: DatasetControlAggregateActionApiRequestData,
  handleApiError: ReturnType<typeof useApiErrorHandling>,
  getResponseActionHandler?: (name: string) => ResponseActionHandler | undefined
) => {
  return async () => {
    const { apiMethod } = control.action as AggregateControlAction;

    // Simple api method flow — params come from the resolved URL template.
    // A string apiMethod has no header templates, so the cached query header
    // scoping the indexes must still be attached here — sourced from the same
    // requestData.cachedQueryId the detailed flow's template resolves.
    if (typeof apiMethod === 'string') {
      const additionalHeaders: Headers = {};
      if (requestData.cachedQueryId)
        additionalHeaders[X_CACHEDQUERY_ID] = requestData.cachedQueryId;

      const headers = Object.keys(additionalHeaders).length
        ? additionalHeaders
        : undefined;

      const { apiUrl } = await resolveTemplate(
        { apiUrl: apiMethod },
        requestData
      );

      return await executeWithForceRetry(
        (forceHeader) =>
          requestDatasetAction(
            apiUrl as SimpleApiMethod,
            'GET',
            token,
            forceHeader,
            headers
          ),
        handleApiError
      );
    }

    // Detailed flow — x-cachedquery-id rides on the apiMethod definition's own
    // header template; requestData already carries cachedQueryId.
    return await executeWithForceRetry(
      (forceHeader) =>
        apiRequest(
          apiMethod as DetailedApiMethod,
          requestData,
          (resolvedApiMethod) =>
            detailedApiMethodFetcher(resolvedApiMethod, forceHeader),
          getResponseActionHandler
        ),
      handleApiError
    );
  };
};
